/*

UGC photo upload, using step3 framework

*/

import React, { useState } from "react"
import ProgressBar from "steps/shared/ProgressBar"
import StageContainer from "./components/StageContainer"
import FrameRow from "./components/FrameRow"

import "./UGCPhotoUploadStep.scss"

import { getUploadImage, saveUploadImage, uploadPhoto } from "services/photo-upload"

import ShareIcons from "components/ShareIcons"
import analytics from "global/AnalyticsData"
import UploadStage from "./components/UploadStage"
import EditStage from "./components/EditStage"
import GalleryScreenHOC from "./components/GalleryHOC"
import UploadCTA from "./components/UploadCTA"

const UGCPhotoUpload = props => {
  const { brk, breakId, step, onEngage = function () {}, onAwardPoints = function () {}, skipButton, nextStepButton, onStepSkip } = props
  const frames = step.ugcCampaign.overlayImages
  const [stepSkipped, setStepSkipped] = useState(false)
  const [frameSelected, setFrameSelected] = useState(null)
  const [activeStage, setActiveStage] = useState(0)
  const [userUpload, setUserUpload] = useState(null)
  const [photoSaved, setPhotoSaved] = useState(null)
  const [shareIcons, setShareIcons] = useState(null)
  const [editingUserImage, setEditingUserImage] = useState(false)
  const [progress, setProgress] = useState(1)
  const handleNextClick = async () => {}
  const handleIsOnScreen = async () => {}

  //If we want to go the route of loading localstorage image on step load.
  const checkSavedImage = async () => {
    let savedPhotoInfo = await getUploadImage(breakId)
    if (savedPhotoInfo && savedPhotoInfo.userUpload) {
      setUserUpload(savedPhotoInfo.userUpload)
    }
    if (savedPhotoInfo && savedPhotoInfo.frame) {
      setFrameSelected(savedPhotoInfo.frame)
    }
  }

  const handleFrameSelect = frame => {
    console.log("event: step_interaction_frame_select_option")
    analytics.stepEvent("step_interaction_frame_select_option", step, {
      result_id: frame.image.id,
      result: frame.image.id,
    })
    setFrameSelected(frame)
  }

  const handleDefaultFrameSelect = () => {
    if (frameSelected === null) {
      setFrameSelected(frames[0])
    }
  }

  const handleImageUpload = async data => {
    console.log("event: step_interaction_upload_photo")
    analytics.stepEvent("step_interaction_upload_photo", step)
    await setUserUpload(data)
    await setEditingUserImage(false)
    if (activeStage === 0) {
      if (frameSelected === null) {
        await setFrameSelected(frames[0])
      }
      await setActiveStage(1)
      await setProgress(2)
    }
  }

  const handleShareClick = ({ platform }) => {
    analytics.stepEvent("step_interaction_photo_share_option", step, {
      result_id: platform,
      result: platform,
    })
  }

  const handlePhotoSave = async savedData => {
    console.log("event: step_interaction_photo_save")
    analytics.stepEvent("step_interaction_photo_save", step, {
      source: "UGC",
      file_type: "photo",
    })

    if (savedData) {
      let savePhotoObject = {
        imgData: savedData,
      }
      setPhotoSaved(savedData)

      try {
        let results = await saveUploadImage(breakId, savePhotoObject)
        const uploadResponse = await uploadPhoto(savedData, breakId, step.id)
        const uploadResponseBody = await uploadResponse.json()

        const socialShareComponent = step.socialShareOn && <ShareIcons stepId={step.id} submissionId={uploadResponseBody.submissionId} facebookShare={step.facebookShare} twitterShare={step.twitterShare} pinterestShare={step.pinterestShare} emailShare={step.emailShare} copyShare={step.copyShare} whatsappShare={step.whatsappShare} download={savedData || null} /*send dataurl to icons for desktop dl */ shareUrl={brk ? brk.shareUrl : ""} onShareClick={handleShareClick} />

        setShareIcons(socialShareComponent)
        // TODO: would be great to combine these 2 setStates ( setShareIcons & setPhotoSaved ). I agree
      } catch (e) {
        console.log(e)
      }
    }
    await onEngage()
    await onAwardPoints()
    await setProgress(3)
  }

  const parseStageHeadlines = () => {
    if (step.ugcCampaign && step.ugcCampaign.instructions) {
      return step.ugcCampaign.instructions.map(function (elem) {
        return elem.headline
      })
    } else {
      return []
    }
  }

  const handleGotoGallery = () => {
    setActiveStage(2)
    setProgress(null)
  }

  //When user clicks "camera" button to edit photo in stage "2"
  const handleToggleUploadPhotoInEditStage = () => {
    if (photoSaved !== null) {
      setPhotoSaved(null)
    }
    setEditingUserImage(true)
  }

  return (
    <section className="ugc-photo-upload-step">
      <div className={`step-content with-shadow`}>
        <div className={`ugc-header-zone`}>
          <div className="photo-header">
            <div className="title-row">
              {step.ugcCampaign.instructions[activeStage] && <h1>{step.ugcCampaign.instructions[activeStage].headline}</h1>}
              {progress != null && <ProgressBar total={3} position={progress} />}
            </div>
            {step.ugcCampaign.instructions[activeStage] && <p className={`description-active-stage`}>{step.ugcCampaign.instructions[activeStage].desc}</p>}
          </div>
        </div>

        <StageContainer itemActive={activeStage}>
          <UploadStage>
            <UploadCTA onGetProperFile={handleImageUpload} frameSelected={frameSelected} />
          </UploadStage>
          <EditStage frame={frameSelected} userUpload={userUpload} onPhotoSaved={handlePhotoSave} onGotoGallery={handleGotoGallery} galleryButtonText={step.ugcCampaign.galleryBtnTxt || ""} photoSaved={photoSaved} setPhotoSaved={setPhotoSaved} legalText={step.ugcCampaign.instructions[activeStage].legal} onChangePhotoInEditStage={handleToggleUploadPhotoInEditStage} editingUserImage={editingUserImage} saveConsentRequired={step.ugcCampaign.userConsentRequired || false} shareIcons={shareIcons}>
            <UploadCTA onGetProperFile={handleImageUpload} />
          </EditStage>
          <GalleryScreenHOC breakId={breakId} stepId={step.id} userUpload={photoSaved || null} />
        </StageContainer>
        {activeStage !== 2 && photoSaved === null && <FrameRow frames={step.ugcCampaign.overlayImages} label={step.ugcCampaign.layersHeadline || null} onFrameSelect={handleFrameSelect} frameSelected={frameSelected} saveConsentRequired={step.ugcCampaign.userConsentRequired || false} />}
        {skipButton}
        {nextStepButton}
      </div>
    </section>
  )
}

export default UGCPhotoUpload
